import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import CartBundleCard from "../components/CartBundleCard";
import { ReactComponent as DeliveryIcon } from "./../assets/svgs/delivery-icon.svg";
import { ReactComponent as AddressSearchIcon } from "./../assets/svgs/address-search-icon.svg";
import { buildCartCheckoutPayload } from "../utils/cartCheckout";
import NewNavbar from "../components/NewNavbar.js";
import Footer from "../components/Footer.js";
import GoogleMapsApiLoader from "../components/GoogleMapsApiLoader";
import {
  decrementQuantity,
  getCartProductAvailableQuantity,
  selectStandaloneCartItems,
  incrementQuantity,
  isCollectionCartProduct,
  removeFromCart,
  selectCartItems,
  groupCartItems
} from "../store/cartSlice";
import { getCartBundlePricing } from "../utils/cartBundle.js";
import { useLoginCreateMutation, usePostPasswordResetMutation, useProfileReadQuery, usePostCartCheckoutMutation } from "../store/api.ts";
import { formatStorePrice, getFirstError, getProductPricing } from "../utils.js";
import QuickPayButton from "../components/QuickPayButton.js";
const CHECKOUT_STEPS = {
  EMAIL: "email",
  DELIVERY: "delivery",
  PAYMENT: "payment",
};

const CART_SHIPPING_ADDRESS_KEY = "cart_shipping_address";
const GUEST_CHECKOUT_EMAIL_KEY = "guest_checkout_email";

const emptyDeliveryForm = {
  firstName: "",
  lastName: "",
  phone: "",
  address1: "",
  address2: "",
  country: "",
  state: "",
  postalCode: "",
  city: "",
  instructions: "",
};

const getStoredAuthUser = () => {
  if (typeof window === "undefined") return null;

  try {
    return JSON.parse(localStorage.getItem("authUser") || "null");
  } catch {
    return null;
  }
};

const getSavedShippingAddress = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(CART_SHIPPING_ADDRESS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error("Failed to read saved shipping address:", error);
    return null;
  }
};

const saveShippingAddress = (deliveryForm) => {
  try {
    localStorage.setItem(
      CART_SHIPPING_ADDRESS_KEY,
      JSON.stringify({
        streetAddress: deliveryForm.address1,
        apartment: deliveryForm.address2,
        city: deliveryForm.city,
        state: deliveryForm.state,
        postalCode: deliveryForm.postalCode,
        country: deliveryForm.country,
      }),
    );
  } catch (error) {
    console.error("Failed to save shipping address:", error);
  }
};

const getProductImage = (product) => {
  const mainImage = Array.isArray(product?.main_images)
    ? product.main_images.find(
        (image) =>
          typeof image === "string" &&
          image.trim() &&
          image !== "null" &&
          !image.endsWith("/null"),
      )
    : "";

  return (
    mainImage ||
    product?.image ||
    product?.image_url ||
    product?.thumbnail ||
    ""
  );
};

const buildFinalAddress = (values) => {
  const cityStatePostal = [values.city, values.state, values.postalCode]
    .filter(Boolean)
    .join(", ");

  return [values.address1, values.address2, cityStatePostal, values.country]
    .filter((value) => value && String(value).trim())
    .join(", ");
};

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validateDelivery = (values) => {
  const errors = {};

  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  const phone = values.phone.trim();
  const address1 = values.address1.trim();
  const city = values.city.trim();
  const state = values.state.trim();
  const postalCode = values.postalCode.trim();
  const country = values.country.trim();

  const hasLetter = /[A-Za-z]/;
  const hasDigit = /\d/;

  // Guest-only additional fields
  if (!firstName) errors.firstName = "First name is required.";
  if (!lastName) errors.lastName = "Last name is required.";

  if (!phone) errors.phone = "Mobile phone is required.";
  else if (phone.replace(/\D/g, "").length < 7) errors.phone = "Please enter a valid phone number.";

  if (!address1) errors.address1 = "Street Address is required.";
  if (!city) errors.city = "City is required.";
  else if (!hasLetter.test(city)) errors.city = "City must contain letters, not just numbers.";

  if (!state) errors.state = "State is required.";
  else if (!hasLetter.test(state)) errors.state = "State must contain letters, not just numbers.";

  if (!postalCode) errors.postalCode = "ZIP / Postal Code is required.";
  else if (!hasDigit.test(postalCode)) errors.postalCode = "ZIP / Postal Code must contain at least one number.";

  if (!country) errors.country = "Country is required.";
  else if (!hasLetter.test(country)) errors.country = "Country must contain letters, not just numbers.";

  return errors;
};

const OrderSummaryItem = ({ item, onViewProduct }) => {
  const dispatch = useDispatch();

  const product = item?.product || {};
  const quantity = Number(item?.quantity || 1);
  const collectionProduct = isCollectionCartProduct(product);
  const pricing = getProductPricing(product, collectionProduct);
  const unitPrice = Number(pricing?.discountedPrice || 0);
  const lineTotal = unitPrice * quantity;
  const availableQuantity = getCartProductAvailableQuantity(product);
  const hasKnownStock = collectionProduct && availableQuantity !== null;
  const reachedStockLimit = hasKnownStock && quantity >= availableQuantity;
  const image = getProductImage(product);

  const handleRemove = () => {
    dispatch(removeFromCart(product?.id));
    toast.success("Item removed from cart.");
  };

  const handleIncrement = () => {
    if (reachedStockLimit) {
      toast.error(
        availableQuantity === 1
          ? "Only 1 item is available."
          : `Only ${availableQuantity} items are available.`,
      );
      return;
    }

    dispatch(incrementQuantity(product?.id));
  };

  return (
    <div className="rounded-[5px] border border-[#E5E5E5] bg-white px-5 py-5">
      <div className="flex gap-4">
        <div className="h-[129px] w-[94px] shrink-0 overflow-hidden rounded-[5px] bg-[#D6D6D6]">
          {image ? (
            <img
              src={image}
              alt={product?.name || "Product"}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate font-helvena text-[14px] font-medium text-[black]">
            {product?.brand || product?.name || "Product"}
          </p>

          {product?.brand && product?.name ? (
            <p className="mt-[5px] line-clamp-2 font-helvena text-[11px] text-[#1A1A26]">
              {product.name}
            </p>
          ) : null}

          <div className="mt-1 font-spaceMono text-[12px] leading-[15px] text-[#3D648B]">
            {product?.size ? <p>Size: {product.size}</p> : null}
            {product?.color ? <p>Colour: {product.color}</p> : null}

            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={() => onViewProduct(product)}
                className="font-helvena text-[11px] text-[#595973] underline underline-offset-2"
              >
                View Product
              </button>
              <span className="text-[#C7C7C7]">|</span>
              <button
                type="button"
                onClick={handleRemove}
                className="font-helvena text-[11px] text-[#A34848] underline underline-offset-2 transition-opacity hover:opacity-70"
              >
                Remove
              </button>
            </div>
          </div>

          <div className="mt-2 flex items-end justify-between gap-3">
            <div className="flex items-center gap-4">
              <button
                type="button"
                disabled={quantity <= 1}
                onClick={() => dispatch(decrementQuantity(product?.id))}
                className="text-[18px] leading-none text-[#313131] disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Decrease quantity"
              >
                −
              </button>

              <span className="min-w-[14px] text-center font-spaceMono text-[14px] text-black">
                {quantity}
              </span>

              <button
                type="button"
                onClick={handleIncrement}
                disabled={reachedStockLimit}
                className="text-[18px] leading-none text-[#313131] disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>

            <p className="font-helvena text-[20px] text-[#525252]">
              {formatStorePrice(lineTotal)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderSummary = ({
  cartItems,
  subtotal,
  discount,
  shipping,
  total,
  onViewProduct,
}) => {
  const itemCount = cartItems.reduce(
    (sum, item) => sum + Number(item?.quantity || 1),
    0,
  );
  const { bundles, standaloneItems } = useMemo(() => groupCartItems(cartItems),[cartItems]);

  return (
    <aside className="h-fit rounded-[6px] border border-[#E5E5E5] bg-white p-6 lg:sticky lg:top-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-montserrat text-[18px] font-bold text-[#313131]">
          ORDER SUMMARY
        </h2>

        <span className="font-spaceMono font-bold text-[10px] uppercase text-[#6A7282]">
          {itemCount} ITEM{itemCount === 1 ? "" : "(S)"}
        </span>
      </div>

      <div className="mt-4 border-t border-[#E5E5E5] pt-5">
        <div className="flex items-center justify-between font-spaceMono text-[10px] font-bold uppercase text-[#313131]">
          <span>Subtotal</span>
          <span>{formatStorePrice(subtotal)}</span>
        </div>
        {discount > 0 ? (
          <div className="mt-5 flex items-center justify-between font-spaceMono text-[10px] font-bold uppercase">
            <span className="text-[#6A7282]">
              Discount
            </span>
            <span className="text-[#257A45]">
              -{formatStorePrice(discount)}
            </span>
          </div>
        ) : null}

        <div className="mt-5 flex items-center justify-between font-spaceMono text-[10px] uppercase tracking-[10%] font-bold">
          <span className="text-[#6A7282]">Shipping</span>
          <span className="font-semibold text-[#8F333B]">
            {shipping === 0 ? "FREE" : formatStorePrice(shipping)}
          </span>
        </div>
      </div>

      <div className="mt-5 border-t border-[#E5E5E5] pt-5">
        <div className="flex items-center justify-between font-bold text-[#313131]">
          <span className="font-montserrat text-[16px]">ORDER TOTAL</span>

          <span className="font-spaceMono text-[14px]">
            {formatStorePrice(total)}
          </span>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {bundles.map((bundle) => (
          <CartBundleCard
            key={bundle.id}
            bundle={bundle}
            compact
            onViewProduct={onViewProduct}
          />
        ))}
        {standaloneItems.map((item, index) => (
          <OrderSummaryItem
            key={`${item?.product?.id || index}-${item?.product?.size || ""}-${item?.product?.color || ""}`}
            item={item}
            onViewProduct={onViewProduct}
          />
        ))}
      </div>
    </aside>
  );
};

const CheckoutInput = ({
  label,
  type = "text",
  value,
  onChange,
  name,
  placeholder,
  autoComplete,
  onBlur,
  error = "",
  inputRef,
  required = false,
  showSearchIcon = false,
}) => {
  return (
    <div className="w-full">
      <div
        className={[
          "flex min-h-[54px] w-full items-center",
          "border bg-[#FBFBFB] px-[14px]",
          "transition-colors",
          error
            ? "border-red-500"
            : "border-[#666666] focus-within:border-[#17172A]",
        ].join(" ")}
      >
        <label className="min-w-0 flex-1 cursor-text py-[7px]">
          <span
            className="
              block font-spaceMono text-[10px] font-bold
              uppercase tracking-[0.14em]
              text-[#6A7282]
            "
          >
            {label}
            {required ? <span aria-hidden="true">*</span> : null}
          </span>
          <input
            ref={inputRef}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            autoComplete={autoComplete}
            className="
              mt-[3px] w-full
              border-0 bg-transparent p-0
              font-helvena text-[14px]
              font-normal leading-[20px]
              text-[#313131]
              outline-none
              placeholder:text-[#313131]
              placeholder:opacity-100
              focus:outline-none focus:ring-0
            "
          />
        </label>
        {showSearchIcon ? (
          <span className="ml-3 shrink-0 text-[#313131]">
            <AddressSearchIcon />
          </span>
        ) : null}
      </div>
      {error ? (
        <p className="mt-1 font-helvena text-[12px] text-red-500">{error}</p>
      ) : null}
    </div>
  );
};

const OrderInformationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const allCartItems = useSelector(selectCartItems);
  const standaloneCartItems = useSelector(selectStandaloneCartItems);
  const checkoutScope = location.state?.checkoutMode || "cart";
  const checkoutProductId = location.state?.checkoutProductId;
  const checkoutBundleId = location.state?.checkoutBundleId;
  const checkoutBundleInstanceId = location.state?.checkoutBundleInstanceId;

  const cartItems = useMemo(() => {
    if (checkoutScope === "single") {
      if (!checkoutProductId) {
        return [];
      }

      return standaloneCartItems.filter(
        (item) => {
          const variantId = Number(
            item?.product?.variant_id ??
              item?.product?.id
          );

          return (
            variantId ===
            Number(checkoutProductId)
          );
        },
      );
    }

    if (checkoutScope === "bundle") {
      const bundleLookupId =
        checkoutBundleInstanceId ||
        checkoutBundleId;

      if (!bundleLookupId) {
        return [];
      }

      return allCartItems.filter((item) => {
        const itemBundleLookupId = String(
          item?.bundle?.instanceId ||
          item?.bundle?.id ||
          ""
        );
        return (
          itemBundleLookupId ===
          String(bundleLookupId)
        );
      });
    }
    return allCartItems;
  }, [
    allCartItems,
    standaloneCartItems,
    checkoutScope,
    checkoutProductId,
    checkoutBundleId,
    checkoutBundleInstanceId,
  ]);

  const [authTick, setAuthTick] = useState(0);

  const isAuthenticated = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return Boolean(localStorage.getItem("authToken"));
  }, [authTick]);

  const storedAuthUser = useMemo(() => getStoredAuthUser(), [authTick]);

  const [step, setStep] = useState(() => {
    if (typeof window === "undefined") {
      return CHECKOUT_STEPS.EMAIL;
    }

    return localStorage.getItem("authToken")
      ? CHECKOUT_STEPS.DELIVERY
      : CHECKOUT_STEPS.EMAIL;
  });

  const [checkoutMode, setCheckoutMode] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return localStorage.getItem("authToken") ? "authenticated" : null;
  });

  const [checkoutEmail, setCheckoutEmail] = useState(
    () => getStoredAuthUser()?.email || "",
  );

  const [guestEmail, setGuestEmail] = useState(() => {
    try {
      return localStorage.getItem(GUEST_CHECKOUT_EMAIL_KEY) || "";
    } catch {
      return "";
    }
  });

  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [login, { isLoading: isLoggingIn }] = useLoginCreateMutation();
  const [sendResetLink, { isLoading: isSendingResetLink }] = usePostPasswordResetMutation();
  const [createOrder, { isLoading: isCheckingOut }] = usePostCartCheckoutMutation();

  const { data: profile, error: profileError } = useProfileReadQuery( undefined, { skip: !isAuthenticated });
  const savedAddress = useMemo(() => getSavedShippingAddress(), []);

  const [addressSearch, setAddressSearch] = useState(() => {
    if (!savedAddress) return "";

    return buildFinalAddress({
      address1: savedAddress?.streetAddress || "",
      address2: savedAddress?.apartment || "",
      city: savedAddress?.city || "",
      state: savedAddress?.state || "",
      postalCode: savedAddress?.postalCode || "",
      country: savedAddress?.country || "",
    });
  });

  const [deliveryForm, setDeliveryForm] = useState({
    ...emptyDeliveryForm,
    address1: savedAddress?.streetAddress || "",
    address2: savedAddress?.apartment || "",
    city: savedAddress?.city || "",
    state: savedAddress?.state || "",
    postalCode: savedAddress?.postalCode || "",
    country: savedAddress?.country || "",
  });

  const [deliveryErrors, setDeliveryErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const addressInputRef = useRef(null);

  const orderTotals = useMemo(() => {
    const { bundles, standaloneItems } = groupCartItems(cartItems);
    const totals = {
      subtotal: 0,
      discount: 0,
      finalSubtotal: 0,
    };
    standaloneItems.forEach((item) => {
      const product = item?.product || {};
      const quantity = Number(item?.quantity || 1);
      const pricing = getProductPricing(product,isCollectionCartProduct(product));
      const original = pricing.originalPrice * quantity;
      const final = pricing.discountedPrice * quantity;
      totals.subtotal += original;
      totals.discount += Math.max(
        original - final,
        0,
      );
      totals.finalSubtotal += final;
    });

    bundles.forEach((bundle) => {
      const pricing = getCartBundlePricing(bundle);
      totals.subtotal += pricing.originalSubtotal;
      totals.discount += pricing.totalSavings;
      totals.finalSubtotal += pricing.total;
    });

    return totals;
  }, [cartItems]);

  const subtotal = orderTotals.subtotal;
  const discount = orderTotals.discount;
  const discountedSubtotal = orderTotals.finalSubtotal;
  const shipping = 0;
  const tax = 0;
  const total = discountedSubtotal +  shipping + tax;

  useEffect(() => {
    if (!profile) {
      return;
    }
    setCheckoutEmail(profile?.email || storedAuthUser?.email || checkoutEmail);
    setDeliveryForm((previous) => ({
      ...previous,
      firstName: previous.firstName || profile?.first_name || "",
      lastName: previous.lastName || profile?.last_name || "",
      phone: previous.phone || profile?.phone || profile?.mobile_phone || "",
      address1: previous.address1 || profile?.address_line1 || "",
      address2: previous.address2 || profile?.address_line2 || "",
      city: previous.city || profile?.city || "",
      state: previous.state || profile?.state || "",
      postalCode: previous.postalCode || profile?.postal_code || "",
      country: previous.country || profile?.country || "",
    }));
  }, [profile, storedAuthUser]);

  useEffect(() => {
    if (profileError && profileError?.status !== 404) console.error("Failed to fetch profile during checkout:", profileError);
  }, [profileError]);

  useEffect(() => {
    const handleAuthChanged = () => {
      setAuthTick((previous) => previous + 1);
    };
    window.addEventListener("eveness-auth-changed", handleAuthChanged);
    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
    };
  }, []);

  const handleViewProduct = (product) => {
    const productSlug =
      product?.real_slug ||
      product?.slug ||
      product?.productSlug ||
      product?.product_url ||
      "";

    if (!productSlug) {
      toast.error("Product slug is not available.");
      return;
    }

    const productType = product?.productType || product?.product_type || (String(productSlug).startsWith("mrk-") ? "marketplace" : "product");
    navigate(`/product/${encodeURIComponent(productSlug)}`, {
      state: {
        productId: product?.product_id || product?.id,
        slug: productSlug,
        productSlug,
        productType,
        product_type: productType,
        is_marketplace: productType === "marketplace",
        matchPercentage:
          product?.matchPercentage ??
          product?.match_percentage ??
          product?.matchScore ??
          null,
      },
    });
  };

  const handleContinueAsGuest = (event) => {
    event.preventDefault();

    const email = guestEmail.trim();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    try {
      localStorage.setItem(GUEST_CHECKOUT_EMAIL_KEY, email);
    } catch {}

    setCheckoutEmail(email);
    setCheckoutMode("guest");

    setStep(CHECKOUT_STEPS.DELIVERY);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleLoginChange = (event) => {
    const { name, value } = event.target;

    setLoginForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleReturningCustomerLogin = async (event) => {
    event.preventDefault();
    const email = loginForm.email.trim();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!loginForm.password) {
      toast.error("Please enter your password.");
      return;
    }

    try {
      const response = await login({
        login: {
          email,
          password: loginForm.password,
        },
      }).unwrap();

      if (response?.token) localStorage.setItem("authToken", response.token);
      if (response?.user)  localStorage.setItem("authUser", JSON.stringify(response.user));

      try {
        localStorage.removeItem(GUEST_CHECKOUT_EMAIL_KEY);
      } catch {}

      window.dispatchEvent(new Event("eveness-auth-changed"));
      window.dispatchEvent(
        new CustomEvent("eveness-login-completed", {
          detail: {
            user: response?.user || null,
          },
        }),
      );

      toast.success(response?.message || "Login successful");
      navigate("/cart", {
        replace: true,
      });
    } catch (error) {
      if (error?.data?.requires_verification) {
        toast.error(
          error?.data?.error || "Please verify your account before logging in.",
        );
        return;
      }
      toast.error(getFirstError(error?.data || error) || "Unable to log in. Please check your email and password.");
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = localStorage.getItem("authToken");
    if (token) navigate("/cart", { replace: true });
  }, [navigate]);

  const handleResetPassword = async () => {
    const email = loginForm.email.trim();

    if (!email) {
      toast.error("Enter your email first, then click Reset Password.");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      await sendResetLink({
        passwordResetRequest: {
          email,
        },
      }).unwrap();

      toast.success("Password reset link sent to your email.");
    } catch (error) {
      toast.error(
        getFirstError(error?.data || error) ||
          "Unable to send password reset link.",
      );
    }
  };

  const updateDeliveryField = (field, value) => {
    setDeliveryForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    if (deliveryErrors[field]) {
      setDeliveryErrors((previous) => ({
        ...previous,
        [field]: "",
      }));
    }
  };

  const markTouched = (field) => {
    setTouchedFields((previous) => ({
      ...previous,
      [field]: true,
    }));
    const errors = validateDelivery(deliveryForm);
    setDeliveryErrors(errors);
  };

  const getFieldError = (field) => {
    if (!touchedFields[field]) {
      return "";
    }
    return deliveryErrors[field] || "";
  };

  const handlePlaceSelected = (parsedAddress, formattedAddress) => {
    const nextForm = {
      ...deliveryForm,
      address1: parsedAddress?.streetAddress || deliveryForm.address1,
      address2: parsedAddress?.apartment || deliveryForm.address2,
      city: parsedAddress?.city || deliveryForm.city,
      state: parsedAddress?.state || deliveryForm.state,
      postalCode: parsedAddress?.postalCode || deliveryForm.postalCode,
      country: parsedAddress?.country || deliveryForm.country,
    };

    setAddressSearch(formattedAddress || "");
    setDeliveryForm(nextForm);
    setDeliveryErrors(validateDelivery(nextForm));
  };

  const handleContinueToPayment = () => {
    const errors = validateDelivery(deliveryForm);

    setDeliveryErrors(errors);
    setTouchedFields({
      firstName: true,
      lastName: true,
      phone: true,
      address1: true,
      country: true,
      state: true,
      postalCode: true,
      city: true,
    });

    if (Object.keys(errors).length) {
      toast.error("Please complete the required delivery information.");
      return;
    }

    saveShippingAddress(deliveryForm);
    setStep(CHECKOUT_STEPS.PAYMENT);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleCheckout = async () => {
    const isGuest = checkoutMode === "guest";
    const email = checkoutEmail?.trim() || guestEmail?.trim();

    if (isGuest && (!email || !validateEmail(email))) {
      toast.error("Please enter a valid email address.");
      setStep(CHECKOUT_STEPS.EMAIL);
      return;
    }

    const errors = validateDelivery(deliveryForm);

    if (Object.keys(errors).length > 0) {
      setDeliveryErrors(errors);
      setTouchedFields({
        firstName: true,
        lastName: true,
        phone: true,
        address1: true,
        country: true,
        state: true,
        postalCode: true,
        city: true,
      });
      toast.error("Please complete the required delivery information.");
      setStep(CHECKOUT_STEPS.DELIVERY);
      return;
    }

    if (!cartItems.length) {
      toast.error("Your cart is empty.");
      return;
    }

    const finalAddress = buildFinalAddress(deliveryForm);
    const cart = buildCartCheckoutPayload({
      cartItems,
      address: finalAddress,
      customerFields: isGuest
        ? {
            email,
            first_name: deliveryForm.firstName.trim(),
            last_name: deliveryForm.lastName.trim(),
            phone: deliveryForm.phone.trim(),
          }
        : {},
    });

    try {
      saveShippingAddress(deliveryForm);
      const response = await createOrder({ cart }).unwrap();
      if (response?.url) {
        window.location.href = response.url;
        return;
      }
      toast.error("Checkout URL not found.");
    } catch (error) {
      console.error("Checkout failed:", error);
      toast.error(getFirstError(error?.data || error) || "Unable to start checkout. Please try again." );
    }
  };

  // Quick pay reuses the form the shopper just filled in, so the address and
  // email it charges against are exactly the ones shown on the review step. The
  // wallet is asked for nothing but a card, which is why this placement passes
  // no `express` flag.
  const quickPayItems = useMemo(
    () =>
      cartItems.map((item) => ({
        variant_id: Number(item?.product?.variant_id || item?.product?.id),
        quantity: Number(item?.quantity || 1),
      })),
    [cartItems],
  );

  const quickPayAmount = useMemo(() => Math.round(Number(total) * 100), [total]);

  const quickPayPayer = useMemo(
    () => ({
      email: checkoutEmail?.trim() || guestEmail?.trim() || "",
      address: buildFinalAddress(deliveryForm),
      firstName: deliveryForm.firstName.trim(),
      lastName: deliveryForm.lastName.trim(),
      phone: deliveryForm.phone.trim(),
      // Structured too, so PaymentIntent.shipping can be populated. Our stored
      // address is one flat string and cannot be taken apart again server-side.
      address1: deliveryForm.address1.trim(),
      address2: deliveryForm.address2.trim(),
      city: deliveryForm.city.trim(),
      state: deliveryForm.state.trim(),
      postalCode: deliveryForm.postalCode.trim(),
      country: deliveryForm.country.trim(),
    }),
    [checkoutEmail, guestEmail, deliveryForm],
  );

  const handleEditEmail = () => {
    if (checkoutMode === "authenticated")  return;

    setStep(CHECKOUT_STEPS.EMAIL);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleEditDelivery = () => {
    setStep(CHECKOUT_STEPS.DELIVERY);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleExpressPay = (provider) => {
  };

  if (!cartItems.length) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <NewNavbar />

        <div className="mx-auto w-full max-w-[1200px] px-5 py-16">
          <div className="rounded-[6px] border border-[#DFDFDF] bg-white px-6 py-16 text-center">
            <h1 className="font-helvena text-[24px] font-semibold text-[#313131]">
              Your cart is empty
            </h1>

            <p className="mt-3 text-[13px] text-[#777777]">
              Add something to your bag before continuing to checkout.
            </p>

            <button
              type="button"
              onClick={() => navigate("/marketplace")}
              className="mt-6 h-[42px] rounded-[4px] bg-[#313131] px-8 font-spaceMono text-[10px] uppercase tracking-[0.14em] text-white"
            >
              Continue Shopping
            </button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <NewNavbar bottomNav="checkout" checkoutStep="email" />

      <div className="mx-auto w-full max-w-[1500px] px-4 pb-16 pt-8 sm:px-6 lg:px-10">
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_400px] xl:gap-16">
          {/* LEFT SIDE */}
          <main className="min-w-0">
            {/* Express Pay */}
            {step === CHECKOUT_STEPS.EMAIL ? (
              <>
                <section className="rounded-[8px] bg-white px-5 py-5">
                  <p className="text-center font-helvena text-[16px] font-semibold text-black sm:text-[18px]">
                    Express pay
                  </p>

                  <div className="mx-auto mt-5 grid w-full max-w-[325px] grid-cols-1 gap-[10px]">
                    {/*
                      * Express placement: this sits above the form, so nothing
                      * has been collected yet and the wallet supplies the email
                      * and the address itself.
                      */}
                    <QuickPayButton
                      express
                      className="w-full min-w-0"
                      items={quickPayItems}
                      amount={quickPayAmount}
                      disabled={!cartItems.length}
                      buttonHeight={45}
                      buttonRadius={5}
                      applePayTheme="black"
                    />
                  </div>
                </section>

                <div className="my-4 flex items-center gap-4 text-center justify-center border-b border-[#E5E5E5] pb-[10px]">
                  <span className="font-spaceMono text-[9px] font-semibold uppercase text-[#A34848]">
                    Or
                  </span>
                </div>
              </>
            ) : null}

            {/* EMAIL */}
            {step === CHECKOUT_STEPS.EMAIL ? (
              <section className="rounded-[8px] border border-[#E5E5E5] bg-white px-7 py-8">
                <h2 className="font-montserrat text-[18px] font-bold text-[#313131]">
                  1. EMAIL
                </h2>

                <div className="mt-6 grid gap-10 md:grid-cols-2">
                  {/* Guest */}
                  <form onSubmit={handleContinueAsGuest}>
                    <h3 className="font-montserrat text-[16px] font-bold text-[#313131]">
                      Continue as guest
                    </h3>

                    <p className="mt-4 font-helvena text-[13px] font-normal leading-[100%] text-[#6A7282]">
                      Easily create an account after purchase.
                    </p>

                    <div className="mt-5">
                      <CheckoutInput
                        label="Email"
                        type="email"
                        value={guestEmail}
                        onChange={(event) => setGuestEmail(event.target.value)}
                        placeholder="Email"
                        autoComplete="email"
                      />
                    </div>

                    <label className="mt-5 flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={marketingOptIn}
                        onChange={(event) =>
                          setMarketingOptIn(event.target.checked)
                        }
                        className="mt-[2px] h-[16px] w-[16px] accent-[#A34848]"
                      />

                      <span className="font-spaceMono text-[10px] text-[#6A7282] mt-1">
                        Get in on top-secret Ref news and other cool stuff.
                      </span>
                    </label>

                    <button
                      type="submit"
                      className="mt-5 flex h-[48px] w-full items-center justify-center rounded-[4px] bg-[#313131] font-spaceMono text-[12px] font-bold uppercase text-white transition-opacity hover:opacity-90"
                    >
                      Continue as Guest
                    </button>
                  </form>

                  {/* Returning customer */}
                  <form onSubmit={handleReturningCustomerLogin}>
                    <h3 className="font-montserrat text-[16px] font-bold text-[#313131]">
                      Returning customers
                    </h3>

                    <p className="mt-4 font-helvena text-[13px] leading-[100%] font-normal text-[#6A7282]">
                      Sign in for faster checkout and other benefits.
                    </p>

                    <div className="mt-5">
                      <CheckoutInput
                        label="Email"
                        type="email"
                        name="email"
                        value={loginForm.email}
                        onChange={handleLoginChange}
                        autoComplete="email"
                        placeholder="Email"
                      />
                    </div>
                    <div className="mt-5">
                      <CheckoutInput
                        label="Password"
                        type="password"
                        name="password"
                        value={loginForm.password}
                        onChange={handleLoginChange}
                        autoComplete="current-password"
                        placeholder="Password"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoggingIn}
                      className="mt-5 flex h-[48px] w-full items-center justify-center rounded-[4px] bg-[#313131] font-spaceMono text-[12px] font-bold uppercase text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isLoggingIn ? "Logging in..." : "Login"}
                    </button>

                    <div className="mt-5 text-center">
                      <button
                        type="button"
                        disabled={isSendingResetLink}
                        onClick={handleResetPassword}
                        className="font-spaceMono text-[10px] font-bold uppercase tracking-[0.13em] text-[#8F333B] hover:underline disabled:opacity-60"
                      >
                        {isSendingResetLink ? "Sending..." : "Reset Password"}
                      </button>
                    </div>
                  </form>
                </div>
              </section>
            ) : (
              <section className="rounded-[6px] border border-[#DFDFDF] bg-white px-6 py-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-montserrat text-[18px] font-bold text-[#313131]">
                      1. EMAIL
                    </h2>

                    <p className="mt-5 font-spaceMono text-[12px] font-bold text-[#313131]">
                      {checkoutEmail || storedAuthUser?.email || "Customer"}
                    </p>

                    <p className="mt-2 font-helvena text-[11px] text-[#6A7282]">
                      {checkoutMode === "guest"
                        ? "Check out as a guest. Easily create an account later."
                        : "Signed in for faster checkout and other benefits."}
                    </p>
                  </div>

                  {checkoutMode === "guest" ? (
                    <button
                      type="button"
                      onClick={handleEditEmail}
                      className="font-spaceMono text-[9px] font-semibold uppercase tracking-[0.13em] text-[#A34848] hover:underline"
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
              </section>
            )}

            {/* DELIVERY */}
            <section
              className={[
                "mt-6 rounded-[8px] border border-[#E5E5E5] bg-white",
                step === CHECKOUT_STEPS.DELIVERY ? "px-7 py-8" : "px-6 py-6",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-4">
                <h2 className="font-montserrat text-[18px] font-bold text-[#313131]">
                  2. DELIVERY
                </h2>

                {step === CHECKOUT_STEPS.PAYMENT ? (
                  <button
                    type="button"
                    onClick={handleEditDelivery}
                    className="font-spaceMono text-[9px] font-semibold uppercase tracking-[0.14em] text-[#A34848] transition-opacity hover:opacity-70"
                  >
                    Edit
                  </button>
                ) : null}
              </div>

              {step === CHECKOUT_STEPS.DELIVERY ? (
                <div className="mt-6">
                  <div className="grid grid-cols-1 gap-x-5 gap-y-[18px] md:grid-cols-2">
                    {/* First Name */}
                    <CheckoutInput
                      label="First Name"
                      value={deliveryForm.firstName}
                      onChange={(e) =>
                        updateDeliveryField("firstName", e.target.value)
                      }
                      onBlur={() => markTouched("firstName")}
                      placeholder="First name"
                      autoComplete="given-name"
                      required
                      error={getFieldError("firstName")}
                    />

                    {/* Last Name */}
                    <CheckoutInput
                      label="Last Name"
                      value={deliveryForm.lastName}
                      onChange={(e) =>
                        updateDeliveryField("lastName", e.target.value)
                      }
                      onBlur={() => markTouched("lastName")}
                      placeholder="Last name"
                      autoComplete="family-name"
                      required
                      error={getFieldError("lastName")}
                    />

                    {/* Mobile Phone */}
                    <div className="md:col-span-2">
                      <CheckoutInput
                        label="Mobile Phone"
                        type="tel"
                        value={deliveryForm.phone}
                        onChange={(e) =>
                          updateDeliveryField("phone", e.target.value)
                        }
                        onBlur={() => markTouched("phone")}
                        placeholder="Mobile phone"
                        autoComplete="tel"
                        required
                        error={getFieldError("phone")}
                      />
                    </div>

                    {/* Search Address */}
                    <div className="md:col-span-2">
                      <CheckoutInput
                        label="Search Address"
                        value={addressSearch}
                        onChange={(e) => setAddressSearch(e.target.value)}
                        placeholder="Search address with Google Maps"
                        inputRef={addressInputRef}
                        showSearchIcon
                      />

                      <GoogleMapsApiLoader
                        inputRef={addressInputRef}
                        setAddressSearch={setAddressSearch}
                        onPlaceSelected={handlePlaceSelected}
                      />
                    </div>

                    {/* Address Line 1 */}
                    <CheckoutInput
                      label="Address Line 1"
                      value={deliveryForm.address1}
                      onChange={(e) =>
                        updateDeliveryField("address1", e.target.value)
                      }
                      onBlur={() => markTouched("address1")}
                      placeholder="Street address"
                      autoComplete="address-line1"
                      required
                      error={getFieldError("address1")}
                    />

                    {/* Address Line 2 */}
                    <CheckoutInput
                      label="Address Line 2"
                      value={deliveryForm.address2}
                      onChange={(e) =>
                        updateDeliveryField("address2", e.target.value)
                      }
                      placeholder="Apartment, suite, unit, etc."
                      autoComplete="address-line2"
                    />

                    {/* City */}
                    <CheckoutInput
                      label="City"
                      value={deliveryForm.city}
                      onChange={(e) =>
                        updateDeliveryField("city", e.target.value)
                      }
                      onBlur={() => markTouched("city")}
                      placeholder="City"
                      autoComplete="address-level2"
                      required
                      error={getFieldError("city")}
                    />

                    {/* State */}
                    <CheckoutInput
                      label="State"
                      value={deliveryForm.state}
                      onChange={(e) =>
                        updateDeliveryField("state", e.target.value)
                      }
                      onBlur={() => markTouched("state")}
                      placeholder="State"
                      autoComplete="address-level1"
                      required
                      error={getFieldError("state")}
                    />

                    {/* Country */}
                    <CheckoutInput
                      label="Country"
                      value={deliveryForm.country}
                      onChange={(e) =>
                        updateDeliveryField("country", e.target.value)
                      }
                      onBlur={() => markTouched("country")}
                      placeholder="Country"
                      autoComplete="country-name"
                      required
                      error={getFieldError("country")}
                    />

                    {/* Postal Code */}
                    <CheckoutInput
                      label="Postal Code"
                      value={deliveryForm.postalCode}
                      onChange={(e) =>
                        updateDeliveryField("postalCode", e.target.value)
                      }
                      onBlur={() => markTouched("postalCode")}
                      placeholder="Postal code"
                      autoComplete="postal-code"
                      required
                      error={getFieldError("postalCode")}
                    />
                  </div>
                  <div className="mt-8 flex justify-end">
                    <button
                      type="button"
                      onClick={handleContinueToPayment}
                      className="
                        flex h-[48px] min-w-[250px]
                        items-center justify-center
                        rounded-[4px] bg-[#313131] px-7
                        font-montserrat text-[10px] font-semibold
                        uppercase tracking-[0.15em] text-white
                        transition-opacity hover:opacity-90
                      "
                    >
                      Continue to Payment
                    </button>
                  </div>
                </div>
              ) : null}

              {step === CHECKOUT_STEPS.PAYMENT ? (
                <div className="mt-6">
                  {/* Customer name */}
                  <p className="font-spaceMono text-[12px] font-bold text-[#313131]">
                    {deliveryForm.firstName} {deliveryForm.lastName}
                  </p>
                  {/* Address */}
                  <div className="mt-2 space-y-[2px] font-helvena text-[14px] leading-[100%] text-[#6A7282]">
                    <p>{deliveryForm.address1}</p>

                    {deliveryForm.address2 ? (
                      <p>{deliveryForm.address2}</p>
                    ) : null}

                    <p>
                      {[
                        deliveryForm.city,
                        deliveryForm.state,
                        deliveryForm.postalCode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                    <p>{deliveryForm.country}</p>
                    <p>{deliveryForm.phone}</p>
                  </div>

                  {/* Shipping method */}
                  <div className="mt-5 flex items-center gap-3">
                    <DeliveryIcon />
                    <span className="font-spaceMono text-[12px] font-bold text-[#313131]">
                      Free Standard
                    </span>

                    <span className="mt-[2px] font-spaceMono text-[12px] text-[#6A7282]">
                      (3–7 business days)
                    </span>
                  </div>
                </div>
              ) : null}
            </section>

            {/* PAYMENT */}
            <section
              className={[
                "mt-6 rounded-[8px] border border-[#E5E5E5] bg-white",
                step === CHECKOUT_STEPS.PAYMENT ? "px-7 py-8" : "px-6 py-6",
              ].join(" ")}
            >
              <h2 className="font-montserrat text-[18px] font-bold text-[#313131]">
                3. PAYMENT
              </h2>
              {step === CHECKOUT_STEPS.PAYMENT ? (
                <div className="mt-5">
                  <p className="font-helvena text-[13px] leading-[20px] text-[#7B7B87]">
                    Choose how you would like to complete your payment.
                  </p>

                  {/* Express payment */}
                  <div className="mt-6 grid grid-cols-1 gap-3 text-cente max-w-[325px] mx-auto">
                    {/* Apple Pay / Google Pay */}
                    <QuickPayButton
                      className="w-full min-w-0"
                      items={quickPayItems}
                      amount={quickPayAmount}
                      payer={quickPayPayer}
                      disabled={!cartItems.length}
                      buttonHeight={45}
                      buttonRadius={5}
                      applePayTheme="black"
                    />
                  </div>

                  {/* Divider */}
                  <div className="my-6 flex items-center gap-4">
                    <div className="h-px flex-1 bg-[#E1E1E5]" />
                    <span className="font-spaceMono text-[9px] font-semibold uppercase tracking-[0.14em] text-[#9898A2]">
                      Or
                    </span>
                    <div className="h-px flex-1 bg-[#E1E1E5]" />
                  </div>

                  {/* Normal checkout */}
                  <button
                    type="button"
                    onClick={handleCheckout}
                    disabled={isCheckingOut}
                    className="
                      flex h-[45px] w-full
                      items-center justify-center
                      rounded-[5px] bg-[#313131] px-6
                      font-spaceMono text-[10px] font-semibold
                      uppercase tracking-[0.15em] text-white
                      transition-opacity hover:opacity-90
                      disabled:cursor-not-allowed disabled:opacity-60
                      text-center max-w-[325px] mx-auto"
                  >
                    {isCheckingOut ? "Processing..." : "Continue to Checkout"}
                  </button>

                  <p className="mt-3 text-center font-helvena text-[10px] leading-[15px] text-[#9898A2]">
                    You will continue to our secure payment checkout.
                  </p>
                </div>
              ) : null}
            </section>
          </main>

          {/* RIGHT SIDE */}
          <OrderSummary
            cartItems={cartItems}
            subtotal={subtotal}
            discount={discount}
            shipping={shipping}
            total={total}
            onViewProduct={handleViewProduct}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderInformationPage;
